import UsersAPI from 'remote/users'
import * as UserSelectors from './selectors'
import { getApiEndpoint } from 'data/config/selectors'
import { getApiToken } from 'data/auth/selectors'
import { apiCapableActionCreator } from '../apiActionHelpers'
import { dataFetched } from 'data/graphql/reducer'
import DesktopHelpers from 'helpers/DesktopHelpers'
import {
  setActiveUserId,
  createSuccessToasterMessage,
  createErrorToasterMessage,
} from 'data/ui/actions'

import {
  VIEWER_FETCH_ERROR,
  UPDATE_USER,
  UPDATE_USER_EMAIL,
  UPDATE_USER_ORGANIZATION_ROLE,
  UPDATE_USER_PHOTO,
  ARCHIVE_USER,
  SET_USER_AS_OWNER,
} from './actionTypes'

const DESKTOP_USER_UNSET = null

export const viewerFetchError = error => ({
  type: VIEWER_FETCH_ERROR,
  payload: error,
})

// Not an action to be dispatched with current middlewares
export const fetchViewer = ({ dispatch, getState }, apiToken) => {
  const state = getState()
  const apiEndpoint = getApiEndpoint(state)

  apiToken = apiToken || getApiToken(state)

  const mapUserForBugsnag = user => ({
    archivedAt: user.archivedAt,
    createdAt: user.createdAt,
    email: user.email,
    hasOauth: user.hasOauth,
    organizationRole: user.organizationRole,
    id: user.id,
    oauthProvider: user.oauthProvider,
    organization: user.organization,
  })

  return UsersAPI.fetchMe({ apiEndpoint, apiToken }).then(
    ({ me, getUsersNotifications, showUserPreferences }) => {
      dispatch(dataFetched([getUsersNotifications, showUserPreferences, me]))
      dispatch(setActiveUserId(me.id))
      if (window.bugsnag) {
        window.bugsnag.user = mapUserForBugsnag(me)
      }
      if (
        showUserPreferences.isDesktopUser === DESKTOP_USER_UNSET &&
        DesktopHelpers.isSliteProtocolSupported()
      ) {
        UsersAPI.updateUserPreferences(
          { apiEndpoint, apiToken },
          { isDesktopUser: true }
        )
      }
    },
    error => {
      dispatch(viewerFetchError(new Error('Failed to load user data')))
      if (window.bugsnag) {
        window.bugsnag.notify(error, {
          severity: 'error',
          context: 'app-bootstrap',
        })
      }
      throw error
    }
  )
}

export const updateUser = apiCapableActionCreator(
  userAttributes => ({ apiConfig, getState }) => {
    return {
      type: UPDATE_USER,
      payload: UsersAPI.updateUser(apiConfig, userAttributes),
    }
  }
)

export const updateUserEmail = apiCapableActionCreator(
  (email, password) => ({ apiConfig, getState }) => {
    const state = getState()
    const userId = UserSelectors.getCurrentUserId(state)
    return {
      type: UPDATE_USER_EMAIL,
      payload: UsersAPI.updateUserEmail(apiConfig, userId, email, password),
    }
  }
)

export const updateUserOrganizationRole = apiCapableActionCreator(
  (userId, organizationRole) => ({ apiConfig }) => ({
    type: UPDATE_USER_ORGANIZATION_ROLE,
    payload: UsersAPI.updateUserOrganizationRole(
      apiConfig,
      userId,
      organizationRole
    ),
    meta: {
      userId,
      organizationRole,
    },
  })
)

export const archiveUser = ({ userId }) => ({ dispatch, graphQLClient }) => ({
  type: ARCHIVE_USER,
  payload: graphQLClient
    .mutate({
      query: UsersAPI.archiveUser,
      variables: {
        userId,
      },
    })
    .then(({ data }) => {
      dispatch(dataFetched(data))
    }),
  meta: {
    userId,
  },
})

export const setUserAsOwner = ({ userId }) => ({
  dispatch,
  graphQLClient,
}) => ({
  type: SET_USER_AS_OWNER,
  payload: graphQLClient
    .mutate({
      query: UsersAPI.setUserAsOwner,
      variables: {
        userId,
      },
    })
    .then(
      ({ data }) => {
        dispatch(dataFetched(data))
        dispatch(
          createSuccessToasterMessage({
            title: 'Ownership successfully transferred',
          })
        )
      },
      () =>
        createErrorToasterMessage({
          title: 'Error while transferring ownership',
        })
    ),
  meta: {
    userId,
  },
})

export const updateUserPhoto = apiCapableActionCreator(
  ({ photoFile }) => ({ apiConfig }) => {
    return {
      type: UPDATE_USER_PHOTO,
      payload: UsersAPI.updateUserPhotoURL(apiConfig, { photoFile }),
    }
  }
)
